import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  Download,
  Users,
  Briefcase,
  Calendar,
  MapPin,
  Mail,
  Phone,
  FileText,
  ExternalLink,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Building,
  Clock,
} from "lucide-react";

const AdminCareerPanel = () => {
  const [activeTab, setActiveTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [jobFormData, setJobFormData] = useState({
    title: "",
    location: "",
    department: "",
    type: "Full Time",
    status: "Active",
    description: "",
    requirements: [""],
    responsibilities: [""],
  });

  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    pendingReviews: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch("https://archpoint.onrender.com/api/positions"); // adjust port if needed
        const data = await response.json();
        console.log("Fetched data:", data);
        // Example logic to calculate stats from positions data
        const totalJobs = data.length;
        const activeJobs = data.filter((job) => job.status === "active").length; // assuming 'status' field exists
        const totalApplications = data.reduce(
          (sum, job) => sum + (job.applications?.length || 0),
          0
        );
        const pendingReviews = data.filter(
          (job) => job.reviewStatus === "pending"
        ).length;

        setStats({ totalJobs, activeJobs, totalApplications, pendingReviews });
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };

    fetchStats();
  }, []);

  const departments = [
    "Architecture",
    "Design",
    "Management",
    "Engineering",
    "Sales",
    "Marketing",
    "Finance",
    "HR",
  ];
  const jobTypes = ["Full Time", "Part Time", "Contract", "Internship"];
  const jobStatuses = ["Active", "Draft", "Closed"];
  const applicationStatuses = [
    "Under Review",
    "Interview Scheduled",
    "Hired",
    "Rejected",
  ];

  useEffect(() => {
    // Fetch jobs and applications from backend
    const fetchData = async () => {
      try {
        const jobsRes = await fetch(
          "https://archpoint.onrender.com/api/positions"
        );
        const jobsData = await jobsRes.json();
        setJobs(jobsData);

        const appsRes = await fetch(
          "https://archpoint.onrender.com/api/applications"
        );
        const appsData = await appsRes.json();
        setApplications(appsData);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  const handleJobSubmit = async (e) => {
    e.preventDefault();

    if (
      !jobFormData.title ||
      !jobFormData.location ||
      !jobFormData.department ||
      !jobFormData.description
    ) {
      alert("Please fill in all required fields");
      return;
    }

    const filteredRequirements = jobFormData.requirements.filter(
      (req) => req.trim() !== ""
    );
    const filteredResponsibilities = jobFormData.responsibilities.filter(
      (resp) => resp.trim() !== ""
    );

    const updatedJobData = {
      ...jobFormData,
      requirements: filteredRequirements,
      responsibilities: filteredResponsibilities,
    };

    try {
      if (editingJob) {
        const res = await fetch(
          `https://archpoint.onrender.com/api/positions/${editingJob._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedJobData),
          }
        );
        const updated = await res.json();
        setJobs(jobs.map((job) => (job._id === updated._id ? updated : job)));
      } else {
        const res = await fetch(
          "https://archpoint.onrender.com/api/positions",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedJobData),
          }
        );
        const newJob = await res.json();
        setJobs([...jobs, newJob]);
      }
    } catch (err) {
      console.error("Error saving job:", err);
    }

    setShowJobModal(false);
    setEditingJob(null);
    resetJobForm();
  };

  const resetJobForm = () => {
    setJobFormData({
      title: "",
      location: "",
      department: "",
      type: "Full Time",
      status: "Active",
      description: "",
      requirements: [""],
      responsibilities: [""],
    });
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobFormData(job);
    setShowJobModal(true);
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm("Are you sure you want to delete this job posting?")) {
      try {
        await fetch(`https://archpoint.onrender.com/api/positions/${jobId}`, {
          method: "DELETE",
        });
        setJobs(jobs.filter((job) => job._id !== jobId));
      } catch (err) {
        console.error("Error deleting job:", err);
      }
    }
  };

  const addRequirement = () => {
    setJobFormData((prev) => ({
      ...prev,
      requirements: [...prev.requirements, ""],
    }));
  };

  const removeRequirement = (index) => {
    setJobFormData((prev) => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index),
    }));
  };

  const updateRequirement = (index, value) => {
    setJobFormData((prev) => ({
      ...prev,
      requirements: prev.requirements.map((req, i) =>
        i === index ? value : req
      ),
    }));
  };

  const addResponsibility = () => {
    setJobFormData((prev) => ({
      ...prev,
      responsibilities: [...prev.responsibilities, ""],
    }));
  };

  const removeResponsibility = (index) => {
    setJobFormData((prev) => ({
      ...prev,
      responsibilities: prev.responsibilities.filter((_, i) => i !== index),
    }));
  };

  const updateResponsibility = (index, value) => {
    setJobFormData((prev) => ({
      ...prev,
      responsibilities: prev.responsibilities.map((resp, i) =>
        i === index ? value : resp
      ),
    }));
  };

  const updateApplicationStatus = (applicationId, newStatus) => {
    setApplications(
      applications.map((app) =>
        app._id === applicationId ? { ...app, status: newStatus } : app
      )
    );
  };

  const filteredJobs = jobs.filter(
    (job) =>
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredApplications = applications.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 border-green-200";
      case "Draft":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Closed":
        return "bg-red-100 text-red-800 border-red-200";
      case "Under Review":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Interview Scheduled":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "Hired":
        return "bg-green-100 text-green-800 border-green-200";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const exportData = () => {
    const data = activeTab === "jobs" ? jobs : applications;
    const jsonString =
      "data:text/json;chatset=utf-8," +
      encodeURIComponent(JSON.stringify(data));
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `${activeTab}_data.json`;
    link.click();
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Career Management
              </h1>
              <p className="text-gray-600 mt-1">
                Manage job postings and track applications
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={exportData}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all duration-300 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </button>
              <button
                onClick={() => setShowJobModal(true)}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-all duration-300 flex items-center shadow-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Job
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
       <section className="container mx-auto px-6 py-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500 shadow-lg">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-blue-600 font-medium">Total Jobs</p>
              <p className="text-3xl font-bold text-blue-900">{stats.totalJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm p-6 border border-green-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500 shadow-lg">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-green-600 font-medium">Active Jobs</p>
              <p className="text-3xl font-bold text-green-900">{stats.activeJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm p-6 border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-500 shadow-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-purple-600 font-medium">Total Applications</p>
              <p className="text-3xl font-bold text-purple-900">{stats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl shadow-sm p-6 border border-yellow-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-500 shadow-lg">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-yellow-600 font-medium">Pending Reviews</p>
              <p className="text-3xl font-bold text-yellow-900">{stats.pendingReviews}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* Tabs */}
      <section className="container mx-auto px-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("jobs")}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "jobs"
                    ? "border-yellow-500 text-yellow-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Briefcase className="w-4 h-4" />
                  <span>Job Postings ({jobs.length})</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab("applications")}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "applications"
                    ? "border-yellow-500 text-yellow-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Applications ({applications.length})</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Search and Filter */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={
                    activeTab === "jobs"
                      ? "Search jobs by title, department, or location..."
                      : "Search applications by name, position, or email..."
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                />
              </div>

              {activeTab === "applications" && (
                <div className="relative min-w-0 sm:min-w-[200px]">
                  <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent appearance-none bg-white w-full"
                  >
                    <option value="All">All Status</option>
                    {applicationStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Jobs Tab */}
          {activeTab === "jobs" && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applications
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Posted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredJobs.map((job) => (
                      <tr
                        key={job.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {job.title}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Building className="w-4 h-4 mr-2" />
                            {job.department}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job.location}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {job.type}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                              job.status
                            )}`}
                          >
                            {job.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Users className="w-4 h-4 mr-2 text-gray-400" />
                            {job.applications}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="w-4 h-4 mr-2" />
                            {new Date(job.datePosted).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditJob(job)}
                              className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-50 transition-colors"
                              title="Edit Job"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteJob(job.id)}
                              className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                              title="Delete Job"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredJobs.length === 0 && (
                  <div className="text-center py-12">
                    <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No jobs found
                    </h3>
                    <p className="text-gray-500">
                      Try adjusting your search terms or create a new job
                      posting.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === "applications" && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Experience
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applied Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredApplications.map((application) => (
                      <tr
                        key={application.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg">
                                <span className="text-sm font-bold text-white">
                                  {application.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {application.name}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="w-3 h-3 mr-1" />
                                {application.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {application.position}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {application.experience}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {new Date(
                              application.appliedDate
                            ).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={application.status}
                            onChange={(e) =>
                              updateApplicationStatus(
                                application.id,
                                e.target.value
                              )
                            }
                            className={`text-xs font-semibold rounded-full px-3 py-1 border ${getStatusColor(
                              application.status
                            )} focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                          >
                            {applicationStatuses.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setSelectedApplication(application)}
                            className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-50 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredApplications.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No applications found
                    </h3>
                    <p className="text-gray-500">
                      Try adjusting your search terms or filters.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Job Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <form onSubmit={handleJobSubmit}>
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {editingJob
                        ? "Edit Job Posting"
                        : "Create New Job Posting"}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      Fill in the details below to{" "}
                      {editingJob ? "update" : "create"} your job posting
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowJobModal(false);
                      setEditingJob(null);
                      resetJobForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={jobFormData.title}
                      onChange={(e) =>
                        setJobFormData({
                          ...jobFormData,
                          title: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      placeholder="e.g., Senior Software Developer"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={jobFormData.location}
                      onChange={(e) =>
                        setJobFormData({
                          ...jobFormData,
                          location: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      placeholder="e.g., Mumbai, India"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={jobFormData.department}
                      onChange={(e) =>
                        setJobFormData({
                          ...jobFormData,
                          department: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={jobFormData.type}
                      onChange={(e) =>
                        setJobFormData({ ...jobFormData, type: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      required
                    >
                      {jobTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={jobFormData.status}
                      onChange={(e) =>
                        setJobFormData({
                          ...jobFormData,
                          status: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    >
                      {jobStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={jobFormData.description}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        description: e.target.value,
                      })
                    }
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    placeholder="Describe the role, company culture, and what makes this position attractive..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Requirements
                  </label>
                  <div className="space-y-3">
                    {jobFormData.requirements.map((req, index) => (
                      <div key={index} className="flex gap-3">
                        <input
                          type="text"
                          value={req}
                          onChange={(e) =>
                            updateRequirement(index, e.target.value)
                          }
                          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                          placeholder="Enter requirement"
                        />
                        {jobFormData.requirements.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRequirement(index)}
                            className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addRequirement}
                      className="text-yellow-600 hover:text-yellow-800 text-sm font-medium flex items-center py-2"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Requirement
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Responsibilities
                  </label>
                  <div className="space-y-3">
                    {jobFormData.responsibilities.map((resp, index) => (
                      <div key={index} className="flex gap-3">
                        <input
                          type="text"
                          value={resp}
                          onChange={(e) =>
                            updateResponsibility(index, e.target.value)
                          }
                          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                          placeholder="Enter responsibility"
                        />
                        {jobFormData.responsibilities.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeResponsibility(index)}
                            className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addResponsibility}
                      className="text-yellow-600 hover:text-yellow-800 text-sm font-medium flex items-center py-2"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Responsibility
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowJobModal(false);
                    setEditingJob(null);
                    resetJobForm();
                  }}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 flex items-center justify-center transition-colors shadow-lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingJob ? "Update Job" : "Create Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Application Details
                  </h3>
                  <p className="text-gray-600 mt-1">
                    Complete profile and application information
                  </p>
                </div>
                <button
                  onClick={() => setSelectedApplication(null)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-blue-600" />
                      Personal Information
                    </h4>

                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg">
                            <span className="text-sm font-bold text-white">
                              {selectedApplication.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Full Name</p>
                          <p className="font-semibold text-lg">
                            {selectedApplication.name}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Email Address</p>
                          <p className="font-medium">
                            {selectedApplication.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Phone Number</p>
                          <p className="font-medium">
                            {selectedApplication.phone}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Briefcase className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">
                            Position Applied
                          </p>
                          <p className="font-medium">
                            {selectedApplication.position}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">
                            Application Date
                          </p>
                          <p className="font-medium">
                            {new Date(
                              selectedApplication.appliedDate
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Building className="w-5 h-5 mr-2 text-green-600" />
                      Professional Information
                    </h4>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-600">
                          Experience Level
                        </p>
                        <p className="font-medium">
                          {selectedApplication.experience}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">
                          Educational Background
                        </p>
                        <p className="font-medium">
                          {selectedApplication.education}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">Current Status</p>
                        <span
                          className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(
                            selectedApplication.status
                          )}`}
                        >
                          {selectedApplication.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-purple-600" />
                      Documents & Links
                    </h4>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center">
                          <div className="p-2 bg-red-100 rounded-lg mr-3">
                            <FileText className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Resume</p>
                            <p className="text-xs text-gray-500">
                              {selectedApplication.resume}
                            </p>
                          </div>
                        </div>
                        <button className="text-yellow-600 hover:text-yellow-800 p-2 rounded-lg hover:bg-yellow-50 transition-colors">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>

                      {selectedApplication.linkedinProfile && (
                        <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg mr-3">
                              <ExternalLink className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                LinkedIn Profile
                              </p>
                              <p className="text-xs text-gray-500">
                                Professional network profile
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              window.open(
                                selectedApplication.linkedinProfile,
                                "_blank"
                              )
                            }
                            className="text-yellow-600 hover:text-yellow-800 p-2 rounded-lg hover:bg-yellow-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {selectedApplication.portfolioLink && (
                        <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg mr-3">
                              <ExternalLink className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">Portfolio</p>
                              <p className="text-xs text-gray-500">
                                View work samples
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              window.open(
                                selectedApplication.portfolioLink,
                                "_blank"
                              )
                            }
                            className="text-yellow-600 hover:text-yellow-800 p-2 rounded-lg hover:bg-yellow-50 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      Cover Letter
                    </h4>
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {selectedApplication.coverLetter}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      Update Status
                    </h4>
                    <select
                      value={selectedApplication.status}
                      onChange={(e) => {
                        updateApplicationStatus(
                          selectedApplication.id,
                          e.target.value
                        );
                        setSelectedApplication({
                          ...selectedApplication,
                          status: e.target.value,
                        });
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    >
                      {applicationStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setSelectedApplication(null)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  alert(
                    "Interview scheduling feature would be implemented here"
                  );
                }}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-500 border border-transparent rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Interview
              </button>
              <button
                onClick={() => {
                  window.location.href = `mailto:${selectedApplication.email}?subject=Regarding your application for ${selectedApplication.position}`;
                }}
                className="px-6 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 transition-colors shadow-lg"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCareerPanel;
